/* Dialog for the OPML import flow (podcasts or RSS feeds).
   Routes to OPML import steps or an inline RSS URL form depending on the selected app. */

import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { FEED_APP_INSTRUCTIONS } from "@/lib/podcastAppInstructions";
import { useFeedImport, type SourceImportType } from "@/hooks/usePodcastImport";
import { useFeatureTracking } from "@/hooks/useFeatureTracking";
import { PodcastImportInstructions } from "@/components/sources/PodcastImportInstructions";
import { PodcastImportUpload } from "@/components/sources/PodcastImportUpload";
import { PodcastImportResults } from "@/components/sources/PodcastImportResults";

interface FeedImportDialogProps {
  /** The selected app ID, or null when closed */
  app: string | null;
  /** Source type to create: "podcast" or "rss" */
  sourceType?: SourceImportType;
  onOpenChange: (open: boolean) => void;
}

const FEED_LABELS: Record<SourceImportType, string> = {
  podcast: "podcast",
  rss: "feed",
};

export function FeedImportDialog({
  app,
  sourceType = "podcast",
  onOpenChange,
}: FeedImportDialogProps) {
  const open = app !== null;
  const isManualRss = app === "rss" || app === "rss-url";
  const isOpml = open && !isManualRss;
  const feedLabel = FEED_LABELS[sourceType];

  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isManualRss
              ? `Add ${sourceType === "rss" ? "RSS Feed" : "Podcast RSS Feed"}`
              : `Import ${sourceType === "rss" ? "Feeds" : "Podcasts"}`}
          </DialogTitle>
        </DialogHeader>

        {isOpml && app && (
          <OpmlImportFlow
            app={app}
            sourceType={sourceType}
            feedLabel={feedLabel}
            onDone={handleClose}
          />
        )}
        {isManualRss && (
          <SingleFeedForm sourceType={sourceType} onDone={handleClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** @deprecated Use FeedImportDialog instead */
export const PodcastImportDialog = FeedImportDialog;

/* ── OPML Import Flow ── */

function OpmlImportFlow({
  app,
  sourceType,
  feedLabel,
  onDone,
}: {
  app: string;
  sourceType: SourceImportType;
  feedLabel: string;
  onDone: () => void;
}) {
  const {
    step,
    parsedFeeds,
    parseError,
    isImporting,
    results,
    handleFileSelect,
    handleImport,
    goToUpload,
    reset,
  } = useFeedImport(sourceType);

  // Reset when the app changes
  useEffect(() => {
    reset();
  }, [app, reset]);

  if (step === "instructions") {
    const hasInstructions = app in FEED_APP_INSTRUCTIONS;
    if (hasInstructions) {
      return <PodcastImportInstructions app={app} onContinue={goToUpload} />;
    }
    // Skip to upload if no instructions exist for this app
    goToUpload();
    return null;
  }

  if (step === "upload" || step === "importing") {
    return (
      <PodcastImportUpload
        parsedPodcasts={parsedFeeds}
        parseError={parseError}
        isImporting={isImporting}
        onFileSelect={handleFileSelect}
        onImport={handleImport}
        onBack={reset}
        feedLabel={feedLabel}
      />
    );
  }

  if (step === "results" && results) {
    return (
      <PodcastImportResults
        imported={results.imported}
        skipped={results.skipped}
        importedNames={results.importedNames}
        onDone={onDone}
        feedLabel={feedLabel}
      />
    );
  }

  return null;
}

/* ── Single RSS Feed Form ── */

function SingleFeedForm({
  sourceType,
  onDone,
}: {
  sourceType: SourceImportType;
  onDone: () => void;
}) {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createSource = useMutation(api.sources.create);
  const { trackFeature } = useFeatureTracking();
  const label = sourceType === "rss" ? "Feed" : "Podcast";

  async function handleSubmit() {
    if (!url.trim() || !name.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await createSource({
        name: name.trim(),
        type: sourceType,
        url: url.trim(),
      });
      trackFeature(sourceType === "rss" ? "rss_source_added" : "podcast_source_added");
      onDone();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : `Failed to add ${label.toLowerCase()}.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="feed-url">RSS Feed URL</Label>
        <Input
          id="feed-url"
          placeholder="https://feeds.example.com/feed.xml"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError(null);
          }}
          autoComplete="off"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="feed-name">{label} Name</Label>
        <Input
          id="feed-name"
          placeholder={`My ${label}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      <Button
        onClick={handleSubmit}
        disabled={!url.trim() || !name.trim() || isSubmitting}
        className="w-full"
      >
        {isSubmitting && <Loader2 className="size-4 animate-spin" />}
        Add {label}
      </Button>
    </div>
  );
}
